const { applePayConfigService, mainConfigService } = require('./services/config');
const {
  logoSvg,
  elements,
  method,
  SDK_FOPS,
  OLC_SVG,
} = require('./variables');
const { $t, $payButtonText, $rejectButtonText } = require('./translate');
const { APPLE_PAY_FALLBACKS, LANGUAGES, CARDS_SVG } = require('./constants');
const { escapeHtml, sanitizeUrl } = require('./security');
const { getFriendPayConfig, createIconHTML, createShareButtonHTML } = require('./utils/friendPayUtils');
const { FRIEND_PAY_ICON_SIZES, SHARE_PLATFORMS, FRIEND_PAY_SVG } = require('./constant/friendPayConstants');
const { isWebShareSupported } = require('./utils/share');

const getIconsHtml = (method) => {
  const icons = [];
  if (Array.isArray(method?.pg_icons) && method?.pg_icons?.length > 0) {
    method.pg_icons.forEach(iconObj => {
      // SECURITY: Sanitize URL and escape attributes
      const safeSrc = sanitizeUrl(iconObj.svg || iconObj.webp || '');
      const safeLabel = escapeHtml(iconObj.label || method?.name || '');
      if (safeSrc) {
        icons.push({
          html: `<img src="${safeSrc}" alt="${safeLabel}" />`,
          label: safeLabel
        });
      }
    });
  }

  //Use method.icon if no valid icons were found from pg_icons
  if (icons.length === 0 && method?.icon) {
    // SECURITY: Sanitize single icon URL and escape attributes
    const safeSrc = sanitizeUrl(method.icon);
    const safeName = escapeHtml(method?.name || '');
    if (safeSrc) {
      icons.push({ 
        html: `<img src="${safeSrc}" alt="${safeName}" class="single-icon" />`, 
        label: safeName 
      });
    }
  }
  
  const maxIcons = 5;
  const displayedIcons = icons.slice(0, maxIcons).map(icon => icon.html).join('');
  const remaining = icons.length - maxIcons;

  const moreText = remaining > 0 ? `<span class="more-icons ottu__sdk-more-icons-theme">+${remaining}</span>` : '';
  return displayedIcons + moreText;
};
exports.getIconsHtml = getIconsHtml;

const totalAmountHtml = function () {
  const html = `
    <div class="ottu__sdk-flex amount-box ottu__sdk-align-center card-only-display ottu__sdk-amount-box-theme">
      <span class="${
        elements.amountTitle
      } ottu__sdk-total ottu__sdk-primary-text-theme ottu__sdk-amount-label-theme">${$t(
    'Amount'
  )}</span>
      <div class="ottu_sdk-amount-value">
        <span class="${
          elements.currency
        } ottu__sdk-currency ottu__sdk-amount ottu__sdk-primary-text-theme ottu__sdk-amount-currency-theme"><span class="skeleton-loader"></span></span>
        <span class="${
          elements.amount
        } ottu__sdk-amount ottu__sdk-primary-text-theme ottu__sdk-amount-theme"><span class="skeleton-loader"></span></span>
      </div>
    </div>
   `;
  return html;
};
exports.totalAmountHtml = totalAmountHtml;

const currencyLabelHTML = function (code) {
  return `<span class="${code.toLowerCase()}-symbol">&#xE900;</span>`;
};
exports.currencyLabelHTML = currencyLabelHTML;

const feesHtml = function () {
  const html = `
    <div class="ottu__sdk-flex wd-100">
    <span class="${elements.feesLabel} layout-flex-basis ottu__sdk-fee-label ottu__sdk-primary-text-theme ottu__sdk-fees-label-theme text-align--start">
      <span class="skeleton-loader"></span>
    </span>
    <div class="layout-flex-basis text-align--end">
      ${feesCurrencyHTML()}
    </div>
    </div>
  `;
  return html;
};
exports.feesHtml = feesHtml;

const feesCurrencyHTML = function () {
  const feesAmountBlock = `<span class="${elements.fees} ottu__sdk-primary-text-theme ottu__sdk-fees-theme"><span class="skeleton-loader"></span></span>`;
  const feesCurrencyBlock = `<span class="${elements.feesCurrency} ottu__sdk-currency ottu__sdk-primary-text-theme ottu__sdk-fees-currency-theme"><span class="skeleton-loader"></span></span>`;
  return mainConfigService.get('lang') === LANGUAGES.EN
    ? `
    ${feesCurrencyBlock} 
    ${feesAmountBlock} 
   `
    : `
    ${feesAmountBlock} 
    ${feesCurrencyBlock} 
   `;
};

const html = function (displayMode) {
  const isSplit = displayMode === 'split';

  // Shared section fragments
  const amountSection = `
          <div class="${isSplit ? 'ottu__sdk-split-section' : ''} layout-flex-basis card-only-display" ${isSplit ? 'data-section="amount"' : ''}>
            <div class="ottu__sdk-header ottu__sdk-title-text-theme ottu__sdk-payment-details-heading-theme">${$t(
              'Payment Details'
            )}</div>
              <div id="${elements.totalBlock}" data-testid="ottu-sdk-amount-total" class="ottu__sdk-flex layout">
              <span class="skeleton-loader amount-box-loader">
              </div>
          </div>`;

  const payWithHeader = `
          <div class="ottu__sdk-header2 card-only-display ottu__sdk-title-text-theme ottu__sdk-payment-methods-heading-theme">${$t(
            'Pay With'
          )}</div>`;

  const cardsAndServicesSection = `
          <div id="${elements.cardsAndServicesBlock}" class="ottu__sdk-flex ottu__sdk-flex-column layout-flex-basis justify-start">
              <div id="${
                elements.blockServices
              }" class="wd-100">
                ${paymentMethodLoader()}
              </div>
              <div id="${
                elements.savedCards
              }" class="wd-100">
                ${paymentMethodLoader()}
              </div>
              <div id="${elements.blockWalletGrid}" class="wd-100 d-none"></div>
            </div>`;

  const methodsSection = `
            <div id="${
              elements.blockMethods
            }" class="ottu__sdk-flex layout-flex-basis">
              ${paymentMethodLoader()}
            </div>`;

  const cardSaveSection = `
          <div id="${
            elements.cardSaveBlock
          }" class="ottu__sdk-flex card-only-display layout card-save-block" ${isSplit ? 'data-section="cardSave"' : ''}></div>`;

  const feesSection = `
          <div id="${
            elements.feesBlock
          }" data-testid="ottu-sdk-fees" class="ottu__sdk-flex card-only-display layout" ${isSplit ? 'data-section="fees"' : ''}></div>`;

  const buttonsLoaderSection = `
          <div id="${elements.blockButtonsLoader}">
          <div><span class="skeleton-loader pay-button-loader"></div>
          </div>`;

  const walletButtonsSection = `
          <div class="ottu__sdk-flex wallet-buttons ottu__sdk-wallet-buttons-theme d-none" ${isSplit ? 'data-section="walletButtons"' : ''}>
            <div id="${
              elements.blockAppleButton
            }" class="layout-flex-basis d-none ottu__sdk-flex flex-col layoutButtons ottu__sdk-center apple-pay-button-block ottu__sdk-applePay-theme">
              <span class="skeleton-loader wallet-buttons-loader">
            </div>
            <div id="${
              elements.blockGoogleButton
            }" data-testid="ottu-sdk-googlepay-button" class="layout-flex-basis d-none ottu__sdk-flex layoutButtons ottu__sdk-center google-pay-button ottu__sdk-googlePay-theme">
              <span class="skeleton-loader wallet-buttons-loader">
            </div>
            <div id="${
              elements.blockStcButton
            }" class="layout-flex-basis d-none ottu__sdk-flex layoutButtons ottu__sdk-center stc-pay-button-block ottu__sdk-stcPay-theme">
              <span class="skeleton-loader wallet-buttons-loader">
            </div>
            <div id="${
              elements.blockUrpayButton
            }" class="layout-flex-basis d-none ottu__sdk-flex layoutButtons ottu__sdk-center ur-pay-button-block ottu__sdk-urPay-theme">
              <span class="skeleton-loader wallet-buttons-loader">
            </div>
            <div id="${
              elements.blockSamsungButton
            }" data-testid="ottu-sdk-samsungpay-button" class="layout-flex-basis d-none ottu__sdk-flex layoutButtons ottu__sdk-center samsung-pay-button ottu__sdk-samsungPay-theme">
              <span class="skeleton-loader wallet-buttons-loader">
            </div>
          </div>`;

  const payButtonsSection = `
          <div id="${
            elements.blockButtons
          }" class="layout-flex-basis ottu__sdk-flex flex-col ottu__sdk-btns ottu__sdk-pay-button-block layoutButtons" ${isSplit ? 'data-section="buttons"' : ''} disabled></div>`;

  const termsSection = `
          <div id="${elements.termsSection}" class="d-none"></div>`;

  const pciSectionHtml = `
          <div id="${elements.pciSection}" class="d-none"></div>`;

  let innerHtml;

  if (isSplit) {
    innerHtml = `
        <div class="ottu__sdk-split-view layout">
          <div class="ottu__sdk-split-left">
            ${payWithHeader}
            <div class="ottu__sdk-split-section ottu__sdk-flex layout card-only-display ottu__sdk-column-view ottu__sdk-methods-block-theme" data-section="cardsAndServices">
              ${cardsAndServicesSection}
            </div>
            <div class="ottu__sdk-split-section ottu__sdk-flex layout card-only-display ottu__sdk-column-view ottu__sdk-methods-block-theme" data-section="methods">
              ${methodsSection}
            </div>
            <div id="${elements.blockWallet}" class="wd-100 d-none"></div>
          </div>
          <div class="ottu__sdk-split-right">
            ${amountSection}
            ${cardSaveSection}
            ${termsSection}
            ${feesSection}
            ${walletButtonsSection}
            ${buttonsLoaderSection}
            ${payButtonsSection}
            ${pciSectionHtml}
          </div>
        </div>`;
  } else {
    innerHtml = `
        <div class="layout flex-col" >
          ${amountSection}
          ${payWithHeader}
          <div class="ottu__sdk-flex layout card-only-display ${
            displayMode == 'grid'
              ? 'ottu__sdk-grid-view'
              : 'ottu__sdk-column-view'
          } ottu__sdk-methods-block-theme" >
          ${cardsAndServicesSection}
          ${methodsSection}
          </div>
          <div id="${elements.blockWallet}" class="wd-100 d-none"></div>
          <div class="ottu__sdk-border card-only-display ottu__sdk-border-theme"></div>
          ${cardSaveSection}
          ${termsSection}
          ${feesSection}
          ${buttonsLoaderSection}
          ${walletButtonsSection}
          ${payButtonsSection}
          ${pciSectionHtml}
        </div>`;
  }

  const html = `
    <div id="${elements.sdkChild}">
      <div class="ottu__sdk-container ottu__sdk-main ottu__sdk-position-relative ottu__sdk-main-theme ottu__sdk-container-${displayMode} ${
        displayMode == 'column' ? 'ottu__sdk-shrink' : ''
      }">
        ${innerHtml}
      </div>
    </div>
    <div id="${
      elements.threeDS
    }" class="d-none ottu__sdk-main ottu__sdk-main-theme ottu__sdk-threeDs ${
    displayMode == 'column' ? 'ottu__sdk-shrink' : ''
  }"></div>
    ${htmlModals()}
  `;
  return html;
};
exports.html = html;

const htmlModals = function () {
  const html = `
    <div id="${elements.modal}" class="d-none ottu__sdk-modal ${mainConfigService.get('lang')} ottu__sdk-card-removal-modal-theme"></div>
    <div id="${elements.popupModal}" class="d-none ottu__sdk-error-modal ${mainConfigService.get('lang')} ottu__sdk-info-modal-theme"></div>
    <div id="${elements.mobilePayModal}" class="d-none ottu__sdk-payment-modal ${mainConfigService.get('lang')} ottu__sdk-payment-modal-theme"></div>
    <div id="${elements.modalOverlay}" class="${mainConfigService.get('lang')} ottu__sdk-modal-overlay-theme"></div>
  `;
  return html;
};
exports.htmlModals = htmlModals;

const paymentMethodLoader = function () {
  const html = `
    <div id="${elements.blockMethodsLoader}" class="ottu__sdk-flex flex-col wd-100">
      <span class="skeleton-loader payment-methods-loader"></span>
      <span class="skeleton-loader payment-methods-loader"></span>
    </div> 
  `;
  return html;
};

const saveCardBlock = function () {
  const html = `
    <div id=${elements.savedCardsBlock} class="wd-100 mg-0-auto">
      <div>{%content%}</div>
    </div>
  `;
  return html;
};
exports.saveCardBlock = saveCardBlock;

const applePayNotSupportedHtml = function () {
  return `
    <div id="${elements.block_not_supported}" class="d-flex not-supported">This device doesn't support Apple Pay</div>
  `;
};
exports.applePayNotSupportedHtml = applePayNotSupportedHtml;

const enableCCV = function () {
  const html = `
    <div class="token{%key%} d-none ottu-sdk-cvv-block ${
      mainConfigService.get('displayMode') === 'grid' ? 'grid-view' : ''
    }" data-testid="ottu-sdk-saved-card-cvv-{%key%}">
      <div class="ottu__sdk-input-div ccv-input ottu__sdk-ccv-input-theme ${
        mainConfigService.get('displayMode') === 'grid' ? 'grid-view' : ''
      }" >
        <div id="token{%key%}${elements.cvvIframe}"> 
        </div>
      </div>
      <span class="ottu__sdk-error2">
        <i></i>
      </span>
    </div>
  `;
  return html;
};

const saveCards = function (ccv_required) {
  const html = `
    <div class="ottu__sdk-position-relative ottu__sdk-radio-div">
    <input type="radio" name="token" id="token{%key%}" data-testid="ottu-sdk-saved-card-{%key%}" token="{%token%}" submit_url="{%url%}" class="ottu__sdk-payment {%radioClass%} {%ccvClass%}" value="{%key%}" data-gateway={%gateway%} data-is_expired={%isExpired%} data-type="save" data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}" data-amount="{%amount%}" data-currency_code_default="{%currency_code_default%}" data-ccv_required="{%ccv_required%}" data-is_preferred="{%is_preferred%}" data-pg_code="{%pg_code%}" data-billing="{%billing%}" data-brand="{%brand%}" data-number="{%number%}" data-expiry_month="{%expiry_month%}" data-expiry_year="{%expiry_year%}" data-bin="{%bin%}"/>
      <label for="token{%key%}" class="ottu__sdk-saved-tokens ottu__sdk-methods-theme ottu__sdk-saved-cards-theme">
       ${ccv_required ? '<div class="saved-card-loader" data-card-key="token{%key%}"><span class="cvv-skeleton-loader"></span></div>' : ''}
        <div class="ottu__sdk-flex flex-col wd-100">
          <span class="ottu__sdk-flex justify-start wd-100">
            <div class="ottu__sdk-flex ottu__sdk-card-number-block">
              <span class="align-center ottu__sdk-card-number-block--span">
                <span data-testid="ottu-sdk-saved-card-number-{%key%}" class="ottu__sdk-card-num ottu__sdk-card-num-desktop ottu__sdk-secondary-text-theme ottu__sdk-card-number-theme">{%number%}</span>
                <span data-testid="ottu-sdk-saved-card-number-{%key%}" class="ottu__sdk-card-num ottu__sdk-card-num-mobile ottu__sdk-secondary-text-theme ottu__sdk-card-number-theme">{%numberMobile%}</span>
              </span>
              <span data-testid="ottu-sdk-saved-card-expiry-{%key%}" class="ottu__sdk-expires {%expiryClass%} ottu__sdk-secondary-text-theme ottu__sdk-card-expiry-theme">{%expiry%}</span>
            </div>
          </span>
          </div>
          ${ccv_required ? enableCCV() : ''}
          <img src="{%logo%}" alt="{%alt%}" class="ottu__sdk-save-token-img" />
          <span class="${
            elements.saveCardDeleteButton
          } popupButtons ottu__sdk-delete-card-logo-theme" data-testid="ottu-sdk-saved-card-delete-{%key%}" delete_url="{%delete_url%}" data-number="{%number%}" >${
    logoSvg.delete
  }</span>
      </label>
    </div>
  `;
  return html;
};
exports.saveCards = saveCards;

const paymentMethodBlock = function () {
  const html = `
    <div class="wd-100">
      <div class="ottu__sdk-accordion">{%content%}</div>
    </div>
  `;
  return html;
};
exports.paymentMethodBlock = paymentMethodBlock;

const paymentMethod = function (hasOneIcon,method,showIframe = false) {
  const iframeContainerHtml = showIframe
    ? `
    <div class="ottu__sdk-card-iframe-wrapper ottu__sdk-methods-theme">
      <div id="loader-payment-method{%key%}${elements.cardIframe}" class="method-iframe-loader iframes-loader"></div>
      <div id="payment-method{%key%}${elements.cardIframe}"></div>
    </div>
  `
    : '';
  

  const html = `
    <div id="method{%key%}" data-testid="ottu-sdk-payment-method-{%code%}" class="${elements.accordionItem} ${elements.paymentMethod} ottu__sdk-methods-theme ottu__sdk-redirect-links-theme" data-method-id="id_{%flow%}" name="{%flow%}" {%flow%}="{%code%}" can-save-card="{%savecard%}" submit_url="{%submit_url%}" direct_payment_url="{%direct_payment_url%}" redirect_url="{%redirect_url%}" payment_url="{%payment_url%}" cancel_url="{%cancel_url%}" trandata="{%trandata%}" data-type="method" data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}" data-amount="{%amount%}" data-currency_code_default="{%currency_code_default%}" data-method_type="{%method_type%}" data-billing="{%billing%}" data-use_as_jazz_sdk="{%use_as_jazz_sdk%}">
      <div class="${elements.accordionItemHeader}" >
        <div class="ottu__sdk-flex method-icon-name-block">
          <div class="ottu__sdk-flex ottu__sdk-flex-column ottu__sdk-payment-method-name-wrapper">
            <div class="ottu__sdk-payment-name ottu__sdk-secondary-text-theme ottu__sdk-payment-method-name-theme more_icons">{%name%}</div>
            <div class="ottu__sdk-payment-description">{%method_description%}</div>
          </div>
          <div class="ottu__sdk-method-icons logo-icons">${getIconsHtml(method)}</div>
        </div>
      </div>
    </div>
    <div class="ottu__sdk-iframe-placeholder" id="iframe-container-{%key%}" data-testid="ottu-sdk-card-iframe-{%key%}">
     ${iframeContainerHtml}
    </div>


  `;
  const multipleIconsHtml = `
    <div id="method{%key%}" data-testid="ottu-sdk-payment-method-{%code%}" class="${elements.accordionItem} ${elements.paymentMethod} ottu__sdk-methods-theme ottu__sdk-redirect-links-theme" data-method-id="id_{%flow%}" name="{%flow%}" {%flow%}="{%code%}" can-save-card="{%savecard%}" submit_url="{%submit_url%}" direct_payment_url="{%direct_payment_url%}" redirect_url="{%redirect_url%}" payment_url="{%payment_url%}" cancel_url="{%cancel_url%}" trandata="{%trandata%}" data-type="method" data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}" data-amount="{%amount%}" data-currency_code_default="{%currency_code_default%}" data-method_type="{%method_type%}" data-billing="{%billing%}" data-use_as_jazz_sdk="{%use_as_jazz_sdk%}">
      <div class="${elements.accordionItemHeader}">
        <div class="ottu__sdk-flex wd-100 justify-between method-icon-name-block">
          <span class="ottu__sdk-flex ottu__sdk-flex-column ottu__sdk-payment-method-name-wrapper">
            <span class="ottu__sdk-payment-name ottu__sdk-secondary-text-theme ottu__sdk-payment-method-name-theme">{%name%}</span>
            <span class="ottu__sdk-payment-description">{%method_description%}</span>
          </span>
          <div class="ottu__sdk-method-icons ">${getIconsHtml(method)}</div>
        </div>
      </div>
    </div>
    <div class="ottu__sdk-iframe-placeholder" id="iframe-container-{%key%}" data-testid="ottu-sdk-card-iframe-{%key%}">
      ${iframeContainerHtml}
    </div>
  `;
  return hasOneIcon ? multipleIconsHtml : html;
};
exports.paymentMethod = paymentMethod;

const friendPayMethod = function() {
  const linkIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
<path d="M11.2803 7.74707C11.5467 7.48065 11.9846 7.48065 12.251 7.74707C13.8173 9.31346 13.8173 11.8594 12.251 13.4258L9.30078 16.376C7.73437 17.9423 5.18846 17.9423 3.62207 16.376C2.05565 14.8096 2.05565 12.2627 3.62207 10.6963L4.61426 9.70312C4.63959 10.2528 4.73999 10.7997 4.91895 11.332L4.59277 11.6592C3.55104 12.6924 3.55173 14.3696 4.59277 15.4033V15.4043C5.62619 16.4465 7.30415 16.447 8.33789 15.4053L11.2803 12.4629L11.2793 12.4619C12.321 11.4282 12.3215 9.75119 11.2793 8.71777L11.2773 8.71484L11.1855 8.6084C11.0284 8.38143 11.0284 8.08345 11.1855 7.85645L11.2773 7.74902L11.2803 7.74707ZM10.6963 3.62207C12.2627 2.05565 14.8096 2.05565 16.376 3.62207C17.9423 5.18846 17.9423 7.73437 16.376 9.30078L15.3828 10.2939C15.3574 9.74412 15.2575 9.19781 15.0781 8.6709L15.4062 8.33691L15.4053 8.33594C16.3808 7.36685 16.4423 5.83328 15.5879 4.79395L15.4043 4.59277C14.3707 3.55124 12.6926 3.55087 11.6592 4.59277L8.71777 7.53418C7.67558 8.56789 7.67634 10.2467 8.71875 11.2803L8.7207 11.2822C9.00021 11.5481 9.00021 11.9831 8.7207 12.249L8.71777 12.251C8.45137 12.5174 8.0135 12.5173 7.74707 12.251C6.18065 10.6846 6.18065 8.13771 7.74707 6.57129L10.6963 3.62207Z" fill="#4A4A4A" stroke="white" stroke-width="0.3"/>
</svg>`;

  const friendPayIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="24" viewBox="0 0 36 24" fill="none">
<rect x="0.25" y="0.25" width="35.5" height="23.5" rx="1.75" stroke="#D2D2D2" stroke-width="0.5"/>
<path d="M19.07 10.4101C19.6774 9.56132 20.0041 8.54377 20.0041 7.50005C20.0041 6.45634 19.6774 5.43879 19.07 4.59005C19.6385 4.20201 20.3117 3.99622 21 4.00005C21.9283 4.00005 22.8185 4.3688 23.4749 5.02518C24.1313 5.68156 24.5 6.57179 24.5 7.50005C24.5 8.42831 24.1313 9.31855 23.4749 9.97493C22.8185 10.6313 21.9283 11.0001 21 11.0001C20.3117 11.0039 19.6385 10.7981 19.07 10.4101ZM11.5 7.50005C11.5 6.80782 11.7053 6.13113 12.0899 5.55556C12.4744 4.97998 13.0211 4.53138 13.6606 4.26647C14.3001 4.00157 15.0039 3.93226 15.6828 4.0673C16.3617 4.20235 16.9854 4.5357 17.4749 5.02518C17.9644 5.51466 18.2977 6.1383 18.4327 6.81724C18.5678 7.49617 18.4985 8.1999 18.2336 8.83944C17.9687 9.47899 17.5201 10.0256 16.9445 10.4102C16.3689 10.7948 15.6922 11.0001 15 11.0001C14.0717 11.0001 13.1815 10.6313 12.5251 9.97493C11.8687 9.31855 11.5 8.42831 11.5 7.50005ZM13.5 7.50005C13.5 7.79672 13.588 8.08673 13.7528 8.33341C13.9176 8.58008 14.1519 8.77234 14.426 8.88587C14.7001 8.9994 15.0017 9.02911 15.2926 8.97123C15.5836 8.91335 15.8509 8.77049 16.0607 8.56071C16.2704 8.35093 16.4133 8.08366 16.4712 7.79269C16.5291 7.50172 16.4994 7.20012 16.3858 6.92603C16.2723 6.65194 16.08 6.41767 15.8334 6.25285C15.5867 6.08803 15.2967 6.00005 15 6.00005C14.6022 6.00005 14.2206 6.15809 13.9393 6.43939C13.658 6.7207 13.5 7.10223 13.5 7.50005ZM22 17.0001V19.0001H8V17.0001C8 17.0001 8 13.0001 15 13.0001C22 13.0001 22 17.0001 22 17.0001ZM20 17.0001C19.86 16.2201 18.67 15.0001 15 15.0001C11.33 15.0001 10.07 16.3101 10 17.0001M21.95 13.0001C22.5629 13.4768 23.064 14.0819 23.4182 14.7729C23.7723 15.4639 23.9709 16.2241 24 17.0001V19.0001H28V17.0001C28 17.0001 28 13.3701 21.94 13.0001H21.95Z" fill="black"/>
</svg>`;

  const html = `
    <div class="ottu__sdk-friend-pay-wrapper">
      <div id="method{%key%}" data-testid="ottu-sdk-payment-method-{%code%}" class="${elements.accordionItem} ${elements.paymentMethod} ottu__sdk-friend-pay-method ottu__sdk-methods-theme ottu__sdk-redirect-links-theme" data-method-id="id_{%flow%}" name="{%flow%}" {%flow%}="{%code%}" can-save-card="{%savecard%}" submit_url="{%submit_url%}" redirect_url="{%redirect_url%}" data-type="method" data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}" data-amount="{%amount%}" data-currency_code_default="{%currency_code_default%}" data-method_type="{%method_type%}" data-billing="{%billing%}">
        <div class="${elements.accordionItemHeader}">
          <div class="ottu__sdk-flex align-center justify-between wd-100">
            <div class="ottu__sdk-flex align-center gap-8">
              <span class="ottu__sdk-payment-name ottu__sdk-secondary-text-theme ottu__sdk-payment-method-name-theme">{%name%}</span>
            </div>
            <div class="ottu__sdk-friend-pay-icon">${friendPayIconSvg}</div>
          </div>
        </div>
      </div>
      <div class="ottu__sdk-friend-pay-body">
        <div class="ottu__sdk-friend-pay-hint-content">
          <div class="ottu__sdk-link-icon">${linkIconSvg}</div>
          <p class="ottu__sdk-friend-pay-description ottu__sdk-secondary-text-theme">{%description%}</p>
        </div>
      </div>
    </div>
  `;
  return html;
};
exports.friendPayMethod = friendPayMethod;

const walletPaymentMethod = function (method) {
  const iconUrl = method.icon || '';
  const iconHtml = iconUrl
    ? `<img src="${sanitizeUrl(iconUrl)}" alt="${escapeHtml(method.label || 'Wallet')}" class="ottu__sdk-wallet-icon" />`
    : '';

  const html = `
    <div id="method{%key%}" data-testid="ottu-sdk-wallet-{%code%}" class="${elements.walletWrapper} ${elements.walletMethod} ottu__sdk-wallet-theme ottu__sdk-methods-theme"
      name="{%flow%}" data-method-id="id_{%flow%}"
      data-type="wallet" data-method_type="wallet_service"
      data-wallet-code="{%code%}"
      data-currency_code="{%currency_code%}"
      data-currency_code_default="{%currency_code_default%}"
      data-billing="{%billing%}">
      <div class="ottu__sdk-wallet-header">
        <div class="ottu__sdk-flex align-center justify-between wd-100">
          <div class="ottu__sdk-flex align-center">
            <label class="switch ottu__sdk-wallet-toggle-theme" onclick="event.stopPropagation()">
              <input type="checkbox" data-testid="ottu-sdk-wallet-toggle-{%code%}" data-wallet-code="{%code%}" class="${elements.walletToggle}" />
              <span class="slider round ottu__sdk-checkbox-theme"></span>
            </label>
            <span class="ottu__sdk-payment-name ottu__sdk-secondary-text-theme ottu__sdk-payment-method-name-theme">{%name%}</span>
          </div>
          <div class="ottu__sdk-method-icons logo-icons">${iconHtml}</div>
        </div>
      </div>
      <div class="${elements.walletBalanceInfo} ottu__sdk-wallet-balance-theme">
        <span>${$t('Available balance:')} <span data-testid="ottu-sdk-wallet-balance-{%code%}" class="ottu__sdk-wallet-balance-amount">{%available_balance%}</span></span>
        <span class="ottu__sdk-wallet-info-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="#2196F3" stroke-width="1.2"/>
            <text x="8" y="12" text-anchor="middle" font-size="10" font-weight="bold" fill="#2196F3">i</text>
          </svg>
          <span class="ottu__sdk-wallet-info-tooltip ottu__sdk-wallet-info-tooltip-theme">${$t('Pay faster using funds already in your wallet. If your balance doesn\'t cover the full amount, you can pay the rest with another method.')}</span>
        </span>
      </div>
      <div class="${elements.walletDetails} ottu__sdk-wallet-details-theme">
        <div class="ottu__sdk-wallet-details-inner">
          <hr class="ottu__sdk-wallet-separator" />
          <div class="ottu__sdk-wallet-detail-row">
            <span class="ottu__sdk-wallet-detail-label">${$t('Balance:')}</span>
            <span class="ottu__sdk-wallet-detail-value ottu__sdk-wallet-balance-change"></span>
          </div>
          <div class="ottu__sdk-wallet-detail-row">
            <span class="ottu__sdk-wallet-detail-label">${$t('Applied:')}</span>
            <span data-testid="ottu-sdk-wallet-applied-{%code%}" class="ottu__sdk-wallet-detail-value ottu__sdk-wallet-applied-value"></span>
          </div>
        </div>
      </div>
    </div>
  `;
  return html;
};
exports.walletPaymentMethod = walletPaymentMethod;

const friendPaySheetModal = function() {
  // Get configuration using helper
  const config = getFriendPayConfig();
  const { orderTotal } = config;

  // Generate icons using helper
  const clockIconSvg = createIconHTML(FRIEND_PAY_SVG.clock, 'Clock', FRIEND_PAY_ICON_SIZES.LARGE);
  const checkIconSvg = createIconHTML(FRIEND_PAY_SVG.checkCircle, 'Check', FRIEND_PAY_ICON_SIZES.SMALL);
  const mailIconSvg = createIconHTML(FRIEND_PAY_SVG.mail, 'Mail', FRIEND_PAY_ICON_SIZES.SMALL);
  const arrowRightSvg = createIconHTML(FRIEND_PAY_SVG.arrowRight, 'Arrow', FRIEND_PAY_ICON_SIZES.EXTRA_LARGE);
  const closeIconSvg = createIconHTML(FRIEND_PAY_SVG.close, 'Close', FRIEND_PAY_ICON_SIZES.EXTRA_LARGE);
  const updateIconSvg = createIconHTML(FRIEND_PAY_SVG.update, 'Update', FRIEND_PAY_ICON_SIZES.SMALL);

  const html = `
    <div id="ottu__sdk-friend-pay-sheet" class="ottu__sdk-friend-pay-sheet">
      <div class="ottu__sdk-friend-pay-sheet-overlay"></div>
      <div class="ottu__sdk-friend-pay-sheet-content">
        <!-- Header -->
        <div class="ottu__sdk-friend-pay-sheet-header">
          <div class="ottu__sdk-friend-pay-icon-wrapper">${clockIconSvg}</div>
          <div class="ottu__sdk-friend-pay-sheet-title-section">
            <h2 id="ottu__sdk-friend-pay-sheet-heading" class="ottu__sdk-friend-pay-modal-heading-theme">${$t('Ask a friend to pay to complete payment')}</h2>
            <p class="ottu__sdk-friend-pay-order-total ottu__sdk-friend-pay-text-regular-14 ottu__sdk-friend-pay-modal-text-theme">${$t('Order total:')} ${orderTotal}</p>
          </div>
          <button data-testid="ottu-sdk-friendpay-close" class="ottu__sdk-friend-pay-sheet-close">${closeIconSvg}</button>
        </div>

        <!-- Timer -->
        <div class="ottu__sdk-friend-pay-timer-container">
          <p class="ottu__sdk-friend-pay-timer-label-theme">${$t('Please complete payment within')}</p>
          <div class="ottu__sdk-friend-pay-countdown ottu__sdk-friend-pay-countdown-theme">05:58:43</div>
        </div>

        <!-- Info Hint -->
        <div id="ottu__sdk-friend-pay-info-hint" class="ottu__sdk-friend-pay-info-hint">
          ${checkIconSvg}
          <p class="ottu__sdk-friend-pay-text-regular-14 ottu__sdk-friend-pay-modal-text-theme">${$t('Your order is created but not paid yet. Share the link with a friend so they can pay for you.')}</p>
        </div>

        <!-- Buttons -->
        <div id="ottu__sdk-friend-pay-actions" class="ottu__sdk-friend-pay-actions">
          <button id="ottu__sdk-friend-pay-send-btn" data-testid="ottu-sdk-friendpay-send" class="ottu__sdk-friend-pay-btn-primary ottu__sdk-friend-pay-send-btn ottu__sdk-friend-pay-btn-primary-theme">
            ${$t('Send to friend')} ${arrowRightSvg}
          </button>
          <button id="ottu__sdk-friend-pay-change-btn" data-testid="ottu-sdk-friendpay-change" class="ottu__sdk-friend-pay-btn-secondary ottu__sdk-friend-pay-change-btn ottu__sdk-friend-pay-btn-secondary-theme">
            ${$t('Change payment method')}
          </button>
        </div>

        <!-- Already paid section -->
        <div id="ottu__sdk-friend-pay-update-link" class="ottu__sdk-friend-pay-update-link">
          <span class="ottu__sdk-friend-pay-modal-text-theme">${$t('Already paid?')}</span>
          <a href="#" id="ottu__sdk-friend-pay-update-btn" data-testid="ottu-sdk-friendpay-update" class="ottu__sdk-friend-pay-modal-link-theme">
            ${updateIconSvg}
            ${$t('Update')}
          </a>
        </div>

        <!-- Divider -->
        <div class="ottu__sdk-friend-pay-divider"></div>

        <!-- Information -->
        <div id="ottu__sdk-friend-pay-info-section" class="ottu__sdk-friend-pay-info-section">
          <div class="ottu__sdk-friend-pay-info-item">
            ${mailIconSvg}
            <p class="ottu__sdk-friend-pay-text-regular-14 ottu__sdk-friend-pay-modal-text-theme">${$t('We\'ll email you a confirmation once payment is complete.')}</p>
          </div>
        </div>
      </div>
    </div>
  `;

  return html;
};
exports.friendPaySheetModal = friendPaySheetModal;

const friendPaySendSheet = function() {
  // Get configuration using helper
  const config = getFriendPayConfig();
  const { orderTotal } = config;

  // Generate close icon using helper
  const closeIconSvg = createIconHTML(FRIEND_PAY_SVG.close, 'Close', FRIEND_PAY_ICON_SIZES.EXTRA_LARGE);

  // Generate share buttons using helper and data-driven approach
  // Filter out 'share' button (Web Share API) if not supported on desktop
  const shareButtonsHtml = SHARE_PLATFORMS
    .filter(platform => {
      // Hide 'share' button if Web Share API is not supported (typically on desktop)
      // This prevents duplicate functionality with 'copy' button which shows same toast
      if (platform.type === 'share' && !isWebShareSupported()) {
        return false;
      }
      return true;
    })
    .map(platform =>
      createShareButtonHTML(platform, FRIEND_PAY_SVG, FRIEND_PAY_ICON_SIZES.MEDIUM)
    ).join('');

  const html = `
    <div id="ottu__sdk-friend-pay-send-sheet" class="ottu__sdk-friend-pay-send-sheet">
      <div class="ottu__sdk-friend-pay-sheet-overlay"></div>
      <div class="ottu__sdk-friend-pay-sheet-content">
        <!-- Header -->
        <div class="ottu__sdk-friend-pay-send-sheet-header">
          <h2 class="ottu__sdk-friend-pay-send-sheet-title ottu__sdk-friend-pay-modal-heading-theme">${$t('Ask a friend to pay for you!')}</h2>
          <button data-testid="ottu-sdk-friendpay-close" class="ottu__sdk-friend-pay-sheet-close">${closeIconSvg}</button>
        </div>

        <!-- Total Amount Container -->
        <div class="ottu__sdk-friend-pay-total-container">
          <p class="ottu__sdk-friend-pay-total-label ottu__sdk-friend-pay-timer-label-theme">${$t('Total to be paid:')}</p>
          <p class="ottu__sdk-friend-pay-total-amount ottu__sdk-friend-pay-total-amount-theme">${orderTotal}</p>
        </div>

        <!-- Message Section -->
        <div class="ottu__sdk-friend-pay-message-section">
          <p class="ottu__sdk-friend-pay-message-hint ottu__sdk-friend-pay-modal-text-theme">${$t('Leave a message encouraging your friend to help you out.')}</p>

          <div class="ottu__sdk-friend-pay-message-box ottu__sdk-friend-pay-message-box-theme">
            <textarea
              id="ottu__sdk-friend-pay-message-textarea"
              data-testid="ottu-sdk-friendpay-message-input"
              class="ottu__sdk-friend-pay-message-textarea ottu__sdk-friend-pay-message-textarea-theme"
              maxlength="200"
              rows="3"
            >${$t('Would you mind helping me out with this payment? Thanks!')}</textarea>
            <div class="ottu__sdk-friend-pay-message-counter">
              <span id="ottu__sdk-friend-pay-char-count">${$t('Would you mind helping me out with this payment? Thanks!').length}</span>/200
            </div>
          </div>

          <a href="#" data-testid="ottu-sdk-friendpay-change-message" class="ottu__sdk-friend-pay-change-message ottu__sdk-friend-pay-change-message-theme">${$t('Change message!')}</a>
        </div>

        <!-- Divider -->
        <div class="ottu__sdk-friend-pay-divider"></div>

        <!-- Share Via Section -->
        <div class="ottu__sdk-friend-pay-share-section">
          <p class="ottu__sdk-friend-pay-share-title ottu__sdk-friend-pay-share-title-theme">${$t('Share Via:')}</p>

          <div class="ottu__sdk-friend-pay-share-buttons">
            ${shareButtonsHtml}
          </div>

          <p class="ottu__sdk-friend-pay-share-hint ottu__sdk-friend-pay-modal-text-theme">${$t('Sharing lets your friend see the order details and complete the payment for you.')}</p>
        </div>

        <!-- Home Indicator (for iOS) -->
        <div class="ottu__sdk-friend-pay-home-indicator"></div>
      </div>
    </div>
  `;

  return html;
};
exports.friendPaySendSheet = friendPaySendSheet;

const friendPayStatusModal = function (iconSvg, heading, subtitle) {
  const html = `
    <div class="ottu__sdk-modal-body ottu__sdk-friend-pay-status-modal-body">
      <span data-testid="ottu-sdk-modal-close" class="ottu__sdk-close-btn popupButtons close-button ottu__sdk-popup-close-button-theme">
        ${logoSvg.modalX}
      </span>
      <div class="ottu__sdk-friend-pay-status-icon">
        ${iconSvg}
      </div>
      <h2 class="ottu__sdk-friend-pay-status-heading">
        ${heading}
      </h2>
      <p data-testid="ottu-sdk-modal-message" class="ottu__sdk-friend-pay-status-subtitle">
        ${subtitle}
      </p>
      <div class="ottu__sdk-modal-btns2 error-popup-btn ottu__sdk-primary-text-theme ottu__sdk-error-retry-button-theme">
        <button data-testid="ottu-sdk-modal-ok" class="close-button popupButtons">${$t('OK')}</button>
      </div>
    </div>
  `;
  return html;
};
exports.friendPayStatusModal = friendPayStatusModal;

const friendPayStatusLoadingModal = function () {
  const html = `
    <div class="ottu__sdk-modal-body ottu__sdk-friend-pay-status-modal-body">
      <div class="ottu__sdk-friend-pay-status-icon">
        ${logoSvg.greyloader}
      </div>
    </div>
  `;
  return html;
};
exports.friendPayStatusLoadingModal = friendPayStatusLoadingModal;

const tamaraWidget = function (name, amount, description = '') {
  const html = `
    <div class="${elements.accordionItemHeader}">
      <div class="ottu__sdk-flex wd-100 justify-between method-icon-name-block">
        <span class="ottu__sdk-flex ottu__sdk-flex-column ottu__sdk-payment-method-name-wrapper">
          <span class="ottu__sdk-tamra-label ottu__sdk-secondary-text-theme ottu__sdk-tamra-label-theme">${name}</span>
          <span class="ottu__sdk-payment-description">${description}</span>
        </span>
        <div id="${elements.tamaraPaymentMethod}" class="ottu__sdk-method-icons ${elements.tamaraWidgetBlock}">
          <tamara-widget amount=${amount} class="ottu__sdk-tamara-widget" type="tamara-summary" inline-variant='text' config='{"badgePosition":"right","showExtraContent":""}'></tamara-widget>
        </div>
      </div>
    </div>
  `
  return html;
}
exports.tamaraWidget = tamaraWidget;

// Generic BNPL widget for payment methods with logo and name
const bnplWidget = function (name, logo, description = '') {
  const html = `
    <div class="${elements.accordionItemHeader}">
      <div class="ottu__sdk-flex wd-100 justify-between method-icon-name-block">
        <span class="ottu__sdk-flex ottu__sdk-flex-column ottu__sdk-payment-method-name-wrapper">
          <span class="ottu__sdk-payment-name ottu__sdk-secondary-text-theme ottu__sdk-payment-method-name-theme">${name}</span>
          <span class="ottu__sdk-payment-description">${description}</span>
        </span>
        <div class="ottu__sdk-method-icons">
          <img src="${logo}" alt="${name}"/>
        </div>
      </div>
    </div>
  `
  return html;
}

// Wrapper functions for backward compatibility
const tabbyWidget = function (name, logo, description = '') {
  return bnplWidget(name, logo, description);
}
exports.tabbyWidget = tabbyWidget;

const talyWidget = function (name, logo, description = '') {
  return bnplWidget(name, logo, description);
}
exports.talyWidget = talyWidget;

const abaWidget = function (name, logo, description = '') {

  const html = `
    <div class="${elements.accordionItemHeader}">
      <div class="ottu__sdk-flex wd-100 justify-between">
        <span class="ottu__sdk-flex ottu__sdk-flex-column">
          <span class="ottu__sdk-payment-name ottu__sdk-secondary-text-theme ottu__sdk-payment-method-name-theme">${name}</span>
          ${description ? `<span class="ottu__sdk-payment-description">${description}</span>` : ''}
        </span>
        <div class="ottu__sdk-method-icons">
          <img src="${logo}" alt="${name}" />
        </div>
      </div>
    </div>
  `;
  return html;
};
exports.abaWidget = abaWidget;

const tamaraPaymentMethod = function (name, amount) {
  const html = `
    <div id="method{%key%}" data-testid="ottu-sdk-payment-method-{%code%}" class="${elements.accordionItem} ${elements.paymentMethod} ottu__sdk-methods-theme ottu__sdk-redirect-links-theme" name="{%flow%}" {%flow%}="{%code%}" submit_url="{%submit_url%}" data-type="method" data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}" data-amount="{%amount%}" data-currency_code_default="{%currency_code_default%}" data-method_type="{%method_type%}" data-billing="{%billing%}">
      ${tamaraWidget(name, amount, '{%method_description%}')}
    </div>
  `;
  return html;
};
exports.tamaraPaymentMethod = tamaraPaymentMethod;

const abaPaymentMethod = function (name, icon, description) {
  const html = `
    <div id="method{%key%}" data-testid="ottu-sdk-method-option-{%key%}" class="${elements.accordionItem} ${elements.paymentMethod} ottu__sdk-methods-theme ottu__sdk-redirect-links-theme"
         name="{%flow%}" {%flow%}="{%code%}" submit_url="{%submit_url%}" data-type="method"
         data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}"
         data-amount="{%amount%}" data-currency_code_default="{%currency_code_default%}" data-sub_option_value="{%sub_option_value%}" data-method_type="{%method_type%}" data-billing="{%billing%}">
      ${abaWidget(name, icon, description)}
    </div>
  `;
  return html;
};
exports.abaPaymentMethod = abaPaymentMethod;

const installmentsNotAvailable = function (logo, flow) {
  const html = `
    <div class="${elements.installmentsNotAvailableBlock} border-radius-12 ottu__sdk-disabled ottu__sdk-methods-theme ottu__sdk-redirect-links-theme align-item--center justify-content">
      <div class="${elements.installmentsNotAvailable}">${flow === SDK_FOPS.TABBY ? $t('Sorry, Tabby is unable to approve this purchase'): $t('Installments Payment option is Not available for you')}</div>
      <img src="${logo}" alt="${flow}" class="ottu__sdk-installment-logo"/>
    </div>
  `;
  return html;
};
exports.installmentsNotAvailable = installmentsNotAvailable;

const loadingInstallmentsOptions = function () {
  const html = `
    <div id="method{%key%}" data-testid="ottu-sdk-payment-method-loading-{%code%}" class="${elements.loadingInstallmentsOptionsBlock} ${elements.paymentMethod} border-radius-10 ottu__sdk-cursor-disabled ottu__sdk-methods-theme ottu__sdk-redirect-links-theme" data-method-id="id_{%flow%}" name="{%flow%}" {%flow%}="{%code%}" submit_url="{%submit_url%}" data-type="method" data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}" data-amount="{%amount%}" data-currency_code_default="{%currency_code_default%}" data-method_type="{%method_type%}">
      <span class="skeleton-loader flex-methods-loader"></span>
    </div>
  `;
  return html;
};
exports.loadingInstallmentsOptions = loadingInstallmentsOptions;

const buttonPay = function () {
  const html = `
    <button type="button" id="${
      elements.payButton
    }" data-testid="ottu-sdk-pay-button" class="ottu__sdk-pay-btn pay-button card-only-display ottu__sdk-primary-text-theme ottu__sdk-pay-button-theme">
      <span>${$payButtonText()}</span>
      <div>${logoSvg.loading}</div>
    </button>
  `;
  return html;
};
exports.buttonPay = buttonPay;

const buttonCancel = function (asLink = false) {
  const commonAttrs = `id="${elements.payCancel}" data-testid="ottu-sdk-cancel-button" class="ottu__sdk-cancel-btn cancel-button card-only-display ottu__sdk-primary-text-theme ottu__sdk-reject-button-theme" data-testid="ottu-sdk-reject"`;
  const inner = `<span>${$rejectButtonText()}</span><div>${logoSvg.greyloader}</div>`;
  const html = asLink 
    ? `<a href="#" ${commonAttrs} role="button">${inner}</a>`
    : `<button type="button" ${commonAttrs}>${inner}</button>`;
  return html;
};
exports.buttonCancel = buttonCancel;

const buttonStc = function () {
  const html = `
    <button id="${elements.stcPayButton}" data-testid="ottu-sdk-stcpay-button" class="stc-pay-button" name="{%flow%}" {%flow%}="{%code%}" can-save-card="{%savecard%}" submit_url="{%submit_url%}" payment_url="{%payment_url%}" data-type="wallet" data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}" data-amount="{%amount%}">
      <img src="{%icon%}" alt=""/>
    </button>
    `;
  return html;
};
exports.buttonStc = buttonStc;

const buttonUrPay = function () {
  const html = `
    <button id="${elements.urPayButton}" data-testid="ottu-sdk-urpay-button" class="ur-pay-button" name="{%flow%}" {%flow%}="{%code%}" can-save-card="{%savecard%}" submit_url="{%submit_url%}" payment_url="{%payment_url%}" data-type="wallet" data-description="{%description%}" data-fee="{%fee%}" data-currency_code="{%currency_code%}" data-amount="{%amount%}">
      <img src="{%icon%}" alt=""/>
    </button>
    `;
  return html;
};
exports.buttonUrPay = buttonUrPay;

const buttonApple = function (isWalletActive) {
  // Check if Apple Pay SDK is available for the new button style
  const useNewButtonStyle = applePayConfigService.get('isApplePaySDKAvailable') && window.ApplePaySession;

  const html = isWalletActive
    ? useNewButtonStyle
      ? `<apple-pay-button id="${elements.applePayButton}" data-testid="ottu-sdk-applepay-button" locale="${
          applePayConfigService.get('buttonLocale') || APPLE_PAY_FALLBACKS.BUTTON_LOCALE
        }" buttonstyle="${
          applePayConfigService.get('buttonColor') || APPLE_PAY_FALLBACKS.BUTTON_COLOR
        }" type="${
          applePayConfigService.get('buttonType') || APPLE_PAY_FALLBACKS.BUTTON_TYPE
        }"></apple-pay-button>`
      : `<button id="${elements.applePayButton}" data-testid="ottu-sdk-applepay-button" lang="${
          applePayConfigService.get('buttonLocale') || LANGUAGES.EN
        }" class="apple-pay-button ottu__sdk-apple-pay-button-type ottu__sdk-apple-pay-button-style ottu__sdk-black-div"></button>`
    : `<button id="${elements.applePayButton}" data-testid="ottu-sdk-applepay-button" lang="${
        applePayConfigService.get('buttonLocale') || LANGUAGES.EN
      }"  class="apple-pay-button ottu__sdk-apple-pay-button-type ottu__sdk-apple-pay-button-style ottu__sdk-black-div"></button>
    <span class="${
      elements.applePayUnavailableText
    } ottu__sdk-secondary-text-theme ottu__sdk-applePay-tooltip-theme" data-testid="ottu-sdk-applepay-unavailable-text">${$t(
        'Apple Pay is unavailable. Please update your device, set up Wallet, add an active card, ensure the domain is verified by Apple, and refresh the page.'
      )}</span>`;
  return html;
};
exports.buttonApple = buttonApple;

const cardRemoveModal = function () {
  const html = `
    <div class="ottu__sdk-modal-header">
      <div class="ottu__sdk-modal-title card-removal-logo">${
        logoSvg.deleteFilled
      }</div>
    </div>
    <div>
      <p data-testid="ottu-sdk-modal-message" class="ottu__sdk-secondary-text-theme ottu__sdk-modal-message ottu__sdk-delete-card-message-theme">
        ${$t('Are you sure you want to remove this method?')}
      </p>
      <div class="ottu__sdk-modal-btns">
      <button data-testid="ottu-sdk-modal-confirm-delete" class="delete-button popupButtons ottu__sdk-primary-text-theme ottu__sdk-delete-card-button-theme">
        <span>${$t('Yes, Remove')}</span>
        <div>${logoSvg.whiteLoader}</div>
      </button>
      <button data-testid="ottu-sdk-modal-keep-card" class="close-button popupButtons ottu__sdk-primary-text-theme ottu__sdk-keep-card-button-theme">${$t(
        'No, Keep'
      )}</button>
      </div>
    </div>
  `;
  return html;
};
exports.cardRemoveModal = cardRemoveModal;

const rejectTransactionModal = function () {
  const html = `
    <div class="ottu__sdk-modal-header">
    </div>
    <div>
      <p data-testid="ottu-sdk-modal-message" class="ottu__sdk-secondary-text-theme ottu__sdk-modal-message">
        ${$t('Are you sure you want to cancel the transaction?')}
      </p>
      <div class="ottu__sdk-modal-btns">
      <button id="${elements.modalYesBtn}" data-testid="ottu-sdk-modal-yes" class="ottu__sdk-yes-button popupButtons ottu__sdk-primary-text-theme">
        <span>${$t('Yes')}</span>
        <div>${logoSvg.whiteLoader}</div>
      </button>
      <button id="${elements.modalNoBtn}" data-testid="ottu-sdk-modal-no" class="ottu__sdk-no-button popupButtons ottu__sdk-primary-text-theme">${$t(
        'No'
      )}</button>
      </div>
    </div>
  `;
  return html;
};
exports.rejectTransactionModal = rejectTransactionModal;


const failedModal = function (heading, buttonText) {
  const html = `
    <div class="ottu__sdk-modal-body">
      <span data-testid="ottu-sdk-modal-close" class="ottu__sdk-close-btn popupButtons close-button ottu__sdk-popup-close-button-theme">
        ${logoSvg.modalX}
      </span>
      <span class="ottu__sdk-payment-failed ottu__sdk-primary-text-theme ottu__sdk-error-popup-heading-theme">
        ${logoSvg.failed}
        ${
          heading
            ? $t(heading)
            : $t('Payment failed...<br/>Error occurred with payment')
        }
      </span>
      <div data-testid="ottu-sdk-modal-message" class="ottu__sdk-modal-error-message ottu__sdk-secondary-text-theme ottu__sdk-error-popup-message-theme">{%message%}</div>
      <div class="ottu__sdk-modal-data ottu__sdk-error-popup-data-theme">{%response%}</div>
      <div class="ottu__sdk-modal-btns2 error-popup-btn ottu__sdk-primary-text-theme ottu__sdk-error-retry-button-theme">
        <button data-testid="ottu-sdk-modal-ok" class="close-button popupButtons">${$t(buttonText || 'Try Again')}</button>
      </div>
    </div>
  `;
  return html;
};
exports.failedModal = failedModal;

const alertModal = function () {
  const html = `
    <div class="ottu__sdk-modal-body">
      <span data-testid="ottu-sdk-modal-close" class="ottu__sdk-close-btn popupButtons close-button ottu__sdk-popup-close-button-theme">
        ${logoSvg.modalX}
      </span>
      <span class="ottu__sdk-alert-heading ottu__sdk-primary-text-theme ottu__sdk-alert-popup-heading-theme">
        ${logoSvg.failed}
        ${$t('Alert')}
      </span>
      <div data-testid="ottu-sdk-modal-message" class="ottu__sdk-modal-error-message ottu__sdk-secondary-text-theme ottu__sdk-alert-popup-message-theme">{%message%}</div>
    </div>
  `;
  return html;
};
exports.alertModal = alertModal;

const redirectModal = function () {
  const html = `
    <div class="ottu__sdk-modal-body">
      <span data-testid="ottu-sdk-modal-message" class="ottu__sdk-redirect-message ottu__sdk-primary-text-theme ottu__sdk-redirect-popup-heading-theme">
        {%message%}
      </span>
      <div class="ottu_sdk-succes-footer">
        ${logoSvg.loader}
      </div>
    </div>
  `;
  return html;
};
exports.redirectModal = redirectModal;

const successModal = function () {
  const html = `
    <div class="ottu__sdk-modal-body">
      <span class="ottu__sdk-payment-success ottu__sdk-primary-text-theme ottu__sdk-success-popup-heading-theme">
        ${logoSvg.success}
        ${$t('Your Payment is done successfully!')}
      </span>
      <span data-testid="ottu-sdk-modal-close" class="ottu__sdk-close-btn popupButtons close-button ottu__sdk-popup-close-button-theme">
        ${logoSvg.modalX}
      </span>
      <p data-testid="ottu-sdk-modal-message" class="ottu__sdk-modal-success-message ottu__sdk-secondary-text-theme ottu__sdk-success-popup-message-theme">{%message%}</p>
      <div class="ottu__sdk-modal-data ottu__sdk-secondary-text-theme ottu__sdk-success-popup-data-theme">{%response%}</div>
      <div class="ottu_sdk-succes-footer">
        ${logoSvg.loader}
      </div>
    </div>
  `;
  return html;
};
exports.successModal = successModal;

const mobilePayModal = function (canSaveCard, saveCard, type) {
  const html = `
    <div class="ottu__sdk-modal-body">
      <span data-testid="ottu-sdk-modal-close" class="ottu__sdk-close-btn popupButtons close-button ottu__sdk-popup-close-button-theme">
        ${logoSvg.modalX}
      </span>
      <span class="ottu__sdk-mobile-number-popup-heading ottu__sdk-primary-text-theme ottu__sdk-mobile-number-popup-heading-theme">
        ${$t('Mobile Number')}
      </span>
      <div id="${
        elements.mobilePayPhoneNumberBlock
      }" class="ottu__sdk-flex flex-col mb-5 align-item--center">
        <div class="ottu__sdk-input-div2 ottu__sdk-mobile-number-input ottu__sdk-primary-text-theme ottu__sdk-mobile-number-input-theme">
          <input id="${
            elements.mobilePayPhoneNumber
          }" data-testid="ottu-sdk-mobilepay-phone-input" class="ottu__sdk-popup-input" type="text" value="{%mobileNo%}" placeholder="${$t(
    'Enter Mobile Number'
  )}"/>
        </div>
        <span class="ottu__sdk-error ottu__sdk-secondary-text-theme ottu__sdk-payment-error-message-theme">
          <i></i>
        </span>
      </div>
      ${canSaveCard === 'true' ? saveMobilePayCheckbox(saveCard, type) : ''}
      <div class="ottu__sdk-modal-btns2 ottu__sdk-primary-text-theme ottu__sdk-otp-send-button-theme">
        <button id="${
          elements.mobilePaySubmitBtn
        }" data-testid="ottu-sdk-mobilepay-phone-submit" class="sendOtpBtn popupButtons">
          <span>${$t('Send OTP')}</span>
          <div>${logoSvg.whiteLoader}</div>
        </button>
      </div>
    </div>
  `;
  return html;
};
exports.mobilePayModal = mobilePayModal;

const mobilePayOTPModal = function () {
  const html = `
    <div class="ottu__sdk-modal-body">
      <span data-testid="ottu-sdk-modal-back" class="ottu__sdk-back-button popupButtons ottu__sdk-popup-back-button-theme">
        ${logoSvg.back}
      </span>
      <span class="ottu__sdk-otp-popup-heading ottu__sdk-primary-text-theme ottu__sdk-otp-popup-heading-theme">
        ${$t('OTP')}
      </span>
      <span data-testid="ottu-sdk-modal-close" class="ottu__sdk-close-btn popupButtons close-button ottu__sdk-popup-close-button-theme">
        ${logoSvg.modalX}
      </span>
      <div id="${
        elements.mobilePayOTPBlock
      }" class="ottu__sdk-flex flex-col align-item--center">
        <div class="ottu__sdk-input-div2 ottu__sdk-otp-input ottu__sdk-primary-text-theme ottu__sdk-otp-input-theme">
          <input id="${
            elements.mobilePayOTP
          }" data-testid="ottu-sdk-mobilepay-otp-input" type="text" class="ottu__sdk-popup-input" placeholder="${$t(
    'Please Enter OTP'
  )}"/>
        </div>
        <span class="ottu__sdk-error ottu__sdk-secondary-text-theme ottu__sdk-payment-error-message-theme">
          <i></i>
        </span>
      </div>
      <button id="${
        elements.mobilePayResendOtpBtn
      }" data-testid="ottu-sdk-mobilepay-otp-resend" class="mobilePayResendOTPBtn ottu__sdk-secondary-text-theme resendButton ottu__sdk-disabled ottu__sdk-otp-resend-button-theme">
        <span>${$t('Resend OTP')}</span>
        <div>${logoSvg.loader}</div>
      </button>
      <div class="ottu__sdk-modal-btns2 ottu__sdk-primary-text-theme ottu__sdk-otp-submit-button-theme">
      <button id="${elements.mobilePayOtpSubmitBtn}" data-testid="ottu-sdk-mobilepay-otp-submit" class="mobilePayButton">
          <span>${$t('Pay')} ${
    method.amount && method.currency_code
      ? ` (${method.amount} ${method.currency_code})`
      : ''
  }</span>
          <div>${logoSvg.whiteLoader}</div>
      </button>
      </div>
    </div>
  `;
  return html;
};
exports.mobilePayOTPModal = mobilePayOTPModal;

const iframeLoader = function () {
  const html = `
    <div id="${elements.blockIframeLoader}" class="ottu__sdk-threeDs-loader">
      <div class="padding-50"><span class="skeleton-loader h-30"></div>
      <div class="padding-50"><span class="skeleton-loader h-30"></div>
      <div class="padding-50"><span class="skeleton-loader h-30"></div>
      <div class="padding-50"><span class="skeleton-loader h-30"></div>
      <div class="padding-50"><span class="skeleton-loader h-30"></div>
      <div class="padding-50"><span class="skeleton-loader h-30"></div>
      <div class="padding-50"><span class="skeleton-loader h-30"></div>
    </div>
  `;
  return html;
};
exports.iframeLoader = iframeLoader;

const paymentProcessingLoader = function () {
  const html = `
    <div class="ottu__sdk-samsung-pay-processing-loader">
      <div class="ottu__sdk-samsung-pay-loader-content">
        <div class="ottu__sdk-samsung-pay-spinner">${logoSvg.greyloader}</div>
        <div class="ottu__sdk-samsung-pay-loader-text">${$t('Processing Payment...')}</div>
      </div>
    </div>
  `;
  return html;
};
exports.paymentProcessingLoader = paymentProcessingLoader;

const saveCardCheckbox = function (disabled) {
  const html = `
  <div class="ottu__sdk-save-token-block ottu__sdk-checkbox-container-theme">
      <label class="switch">
          <input id="${elements.cardSaveCheckbox}" data-testid="ottu-sdk-save-card-checkbox" type="checkbox" checked ${disabled ? 'disabled': ''}/>
          <span class="slider round ottu__sdk-checkbox-theme"></span>
      </label>
    <span class="checkbox-label ottu__sdk-primary-text-theme ottu__sdk-checkbox-label-theme"> ${$t(
      'Save your card for faster future payments'
    )} </span>
  </div>
  `;
  return html;
};
exports.saveCardCheckbox = saveCardCheckbox;

const termsCheckboxBlock = function (termsUrl) {
  const safeUrl = sanitizeUrl(termsUrl || '');
  const html = `
  <div id="${elements.termsBlock}" class="ottu__sdk-terms-block ottu__sdk-terms-container-theme">
    <div class="ottu__sdk-terms-checkbox-row ottu__sdk-checkbox-container-theme">
      <label class="switch">
        <input id="${elements.termsCheckbox}" data-testid="ottu-sdk-terms-checkbox" type="checkbox" />
        <span class="slider round ottu__sdk-checkbox-theme"></span>
      </label>
      <span class="checkbox-label ottu__sdk-primary-text-theme ottu__sdk-checkbox-label-theme">
        ${$t('Agree to')} <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" data-testid="ottu-sdk-terms-link" class="ottu__sdk-terms-link ottu__sdk-terms-link-theme">${$t('Terms & Conditions')}</a>
      </span>
    </div>
  </div>
  `;
  return html;
};
exports.termsCheckboxBlock = termsCheckboxBlock;

const termsNoticeBlock = function (termsUrl) {
  const safeUrl = sanitizeUrl(termsUrl || '');
  const html = `
  <div class="ottu__sdk-terms-notice ottu__sdk-terms-notice-theme">
    <span class="ottu__sdk-primary-text-theme">${$t('By clicking pay button you are accepting our')} <a href="${safeUrl}" target="_blank" rel="noopener noreferrer" data-testid="ottu-sdk-terms-link" class="ottu__sdk-terms-link ottu__sdk-terms-link-theme">${$t('Terms & Conditions')}</a></span>
  </div>
  `;
  return html;
};
exports.termsNoticeBlock = termsNoticeBlock;

const pciLogosSection = function () {
  const html = `
  <div id="${elements.pciLogosBlock}" class="ottu__sdk-pci-logos ottu__sdk-pci-logos-theme">
    ${logoSvg.visaSecure}
    ${logoSvg.mastercardSecure}
    ${logoSvg.pciDss}
  </div>
  <p class="ottu__sdk-pci-disclaimer ottu__sdk-pci-disclaimer-theme">${$t('Our checkout is safe and secure. Your payment information is encrypted and protected by industry-standard security protocols.')}</p>
  `;
  return html;
};
exports.pciLogosSection = pciLogosSection;

const saveMobilePayCheckbox = function (saveCard, type) {
  const html = `
    <div id="${
      elements.mobilePayCheckbox
    }" class="ottu__sdk-save-account-checkbox-block">
      <input
        type="checkbox"
        name="${elements.mobilePaySaveCard}"
        id="${elements.mobilePaySaveCard}"
        data-testid="ottu-sdk-mobilepay-save-account"
        class="ottu__sdk-checkbox"
        ${saveCard ? 'checked' : ''}
      />
      <label for="${
        elements.mobilePaySaveCard
      }" class="ottu__sdk-secondary-text-theme ottu__sdk-save-account-label-theme">${
    type === 'urPay' ? $t('Save URPay Account') : $t('Save STC Account')
  }</label>
    </div>`;
  return html;
};


const clickToPayContainer = function () {
  console.log('click to pay container')
  const html = `
    <div id="${
      elements.blockClickToPayButton
    }" class="layoutButtons ottu__sdk-center">
      <span class="skeleton-loader ctp-buttons-loader"></span>
      <div class="d-none"></div>
    </div>
  `;
  return html;
};

exports.clickToPayContainer = clickToPayContainer;

/**
 * Generate HTML for expired timer state
 * @param {string} title - Escaped title text
 * @param {string} message - Escaped message text
 * @returns {string} HTML string
 */
const timerExpiredHtml = function(title, message) {
  return `
    <div class="ottu__sdk-friend-pay-expired-title ottu__sdk-friend-pay-expired-title-theme">${title}</div>
    <p class="ottu__sdk-friend-pay-expired-message ottu__sdk-friend-pay-expired-message-theme">${message}</p>
  `;
};
exports.timerExpiredHtml = timerExpiredHtml;

/**
 * Generate HTML for timer with more than 1 day remaining
 * @param {string} label - Escaped label text
 * @param {string} placeholder - Escaped placeholder text
 * @param {string} expiresLabel - Escaped "Expires on" label
 * @param {string} expirationDateStr - Escaped expiration date string
 * @returns {string} HTML string
 */
const timerMoreThanDayHtml = function(label, placeholder, expiresLabel, expirationDateStr) {
  return `
    <p class="ottu__sdk-friend-pay-timer-label-theme">${label}</p>
    <div class="ottu__sdk-friend-pay-countdown ottu__sdk-friend-pay-countdown-theme">0 ${placeholder}</div>
    <p class="ottu__sdk-friend-pay-expiration-date">${expiresLabel} ${expirationDateStr}</p>
  `;
};
exports.timerMoreThanDayHtml = timerMoreThanDayHtml;

/**
 * Generate HTML for timer with less than 1 day remaining
 * @param {string} label - Escaped label text
 * @returns {string} HTML string
 */
const timerLessThanDayHtml = function(label) {
  return `
    <p class="ottu__sdk-friend-pay-timer-label-theme">${label}</p>
    <div class="ottu__sdk-friend-pay-countdown ottu__sdk-friend-pay-countdown-theme">00:00:00</div>
  `;
};
exports.timerLessThanDayHtml = timerLessThanDayHtml;

// ─── One Last Check ──────────────────────────────────────────────────────────

const _olcAddressHtml = function(address) {
  const label = escapeHtml(address.label || '');
  const detail = escapeHtml(address.detail || '');
  const pinSvg = OLC_SVG.locationPin;
  const arrowSvg = OLC_SVG.arrowCircleRight;
  return `
    <div class="ottu__sdk-olc-address">
      <div class="ottu__sdk-olc-address-icon">${pinSvg}</div>
      <div class="ottu__sdk-olc-address-body">
        <span class="ottu__sdk-olc-address-label">${label}</span>
        <span class="ottu__sdk-olc-address-detail">${detail}</span>
      </div>
      <div class="ottu__sdk-olc-address-arrow">${arrowSvg}</div>
    </div>
    <hr class="ottu__sdk-olc-divider" />
  `;
};

const _olcItemsHtml = function(items) {
  if (!items.length) return '';
  const currency = escapeHtml(String(mainConfigService.get('currency_code') || ''));
  const rows = items.map((item) => {
    const name = escapeHtml(String(item.name || ''));
    const qty = parseInt(item.quantity, 10) || 1;
    const variant = item.variant ? `<span class="ottu__sdk-olc-item-variant">${escapeHtml(String(item.variant))}</span>` : '';
    const price = item.price != null
      ? `<span class="ottu__sdk-olc-item-price">${currency ? currency + ' ' : ''}${escapeHtml(String(item.price))}</span>`
      : '';
    return `
      <div class="ottu__sdk-olc-item">
        <span class="ottu__sdk-olc-item-qty" aria-hidden="true">${qty}</span>
        <span class="ottu__sdk-olc-item-body">
          <span class="ottu__sdk-olc-item-name">${name}</span>
          ${variant}
        </span>
        ${price}
      </div>`;
  }).join('');
  return `
    <p class="ottu__sdk-olc-items-heading">${escapeHtml($t('Order Items'))} (${items.length})</p>
    <div class="ottu__sdk-olc-items-list">
      ${rows}
    </div>
    <hr class="ottu__sdk-olc-divider" />
  `;
};

const _olcMethodHtml = function() {
  if (!method.type) return '';

  if (method.type === 'token' && method.number) {
    const brand = String(method.brand || '').toUpperCase();
    const iconSrc = CARDS_SVG[brand] ? sanitizeUrl(CARDS_SVG[brand]) : '';
    const iconHtml = iconSrc
      ? `<img class="ottu__sdk-olc-card-icon" src="${iconSrc}" alt="${escapeHtml(brand)}" />`
      : `<span class="ottu__sdk-olc-card-brand">${escapeHtml(brand)}</span>`;
    // Pre-masked by the caller (e.g. "**** 1234") — this just escapes it for HTML output.
    const cardNumberText = `${escapeHtml(String(method.number))}`;
    const expiry = method.expiry_month && method.expiry_year
      ? ` ${$t('Exp')} ${escapeHtml(String(method.expiry_month).padStart(2, '0'))}/${escapeHtml(String(method.expiry_year).slice(-2))}`
      : '';
    return `
      <div class="ottu__sdk-olc-method">
        ${iconHtml}
        <span class="ottu__sdk-olc-method-text">${cardNumberText}${expiry}</span>
      </div>
    `;
  }

  // Onsite (ottu_pg) / redirect / wallet (STC/UR Pay) / other — look up full record for name + icon.
  const paymentMethods = mainConfigService.get('payment_methods') || [];
  const paymentServices = mainConfigService.get('payment_services') || [];
  const found = paymentMethods.find(m => m.code === method.code)
    || paymentServices.find(m => m.code === method.code);

  // icon priority: pg_icons[0] → icons.svg/webp → top-level icon → wallet flow fallback (STC/UR Pay)
  const rawIcon = (found && found.pg_icons && found.pg_icons[0]
      && (found.pg_icons[0].svg || found.pg_icons[0].webp))
    || (found && found.icon)
    || CARDS_SVG[String(method.type).toUpperCase()]
    || '';
  const safeIcon = rawIcon ? sanitizeUrl(rawIcon) : '';
  const displayName = escapeHtml((found && found.name) || method.code || '');

  if (!displayName) return '';

  const iconHtml = safeIcon
    ? `<img class="ottu__sdk-olc-card-icon" src="${safeIcon}" alt="${displayName}" />`
    : '';

  return `
    <div class="ottu__sdk-olc-method">
      ${iconHtml}
      <span class="ottu__sdk-olc-method-text">${displayName}</span>
    </div>
  `;
};

const oneLastCheckSheet = function() {
  const config = mainConfigService.get('oneLastCheck') || {};
  const durationSeconds = config.durationSeconds || 10;
  const items = Array.isArray(config.items) ? config.items : [];
  const address = config.address || null;

  const m = Math.floor(durationSeconds / 60);
  const s = durationSeconds % 60;
  const initialCountdown = `${String(m).padStart(2, '0')}.${String(s).padStart(2, '0')}`;

  return `
    <div id="ottu__sdk-olc-sheet" class="ottu__sdk-olc-sheet" role="dialog" aria-modal="true" aria-labelledby="ottu__sdk-olc-title">
      <div class="ottu__sdk-olc-overlay"></div>
      <div class="ottu__sdk-olc-content">

        <!-- TOP: scrollable order summary -->
        <div class="ottu__sdk-olc-top">
          <h2 id="ottu__sdk-olc-title" class="ottu__sdk-olc-title">${escapeHtml($t('One Last check .....'))} </h2>
          ${address ? _olcAddressHtml(address) : ''}
          ${_olcItemsHtml(items)}
          ${_olcMethodHtml()}
        </div>

        <!-- BOTTOM: sticky progress + action buttons -->
        <div class="ottu__sdk-olc-bottom">
          <div class="ottu__sdk-olc-progress-wrap" aria-hidden="true">
            <div class="ottu__sdk-olc-progress-bar"></div>
          </div>
          <div class="ottu__sdk-olc-actions">
            <button data-testid="ottu-sdk-olc-edit" class="ottu__sdk-olc-btn-edit">
              ${escapeHtml($t('Edit Order'))} &ldquo;<span class="ottu__sdk-olc-countdown" aria-live="polite" aria-atomic="true">${initialCountdown}</span>&rdquo;
            </button>
            <button data-testid="ottu-sdk-olc-confirm" class="ottu__sdk-olc-btn-confirm">
              ${escapeHtml($t('Looks Good'))}
            </button>
          </div>
        </div>

      </div>
    </div>
  `;
};
exports.oneLastCheckSheet = oneLastCheckSheet;